import React, { useEffect, useMemo, useState } from "react";
import {
  api,
  ALL_STATUSES,
  STATUS_META,
  type WorkspaceReelLean,
  type WorkspaceStatus,
} from "../api";
import { PartnerLinkModal } from "../components/PartnerLinkModal";

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;

type Props = {
  onOpen: (id: string) => void;
  onNew: () => void;
  showToast?: ToastFn;
  onShowQueue?: () => void;
};

type Filter = "all" | WorkspaceStatus;
type RenderTheme = "reel" | "dark" | "light" | "both";

export const Workspace: React.FC<Props> = ({ onOpen, onNew, showToast, onShowQueue }) => {
  const [items, setItems] = useState<WorkspaceReelLean[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [partnerTarget, setPartnerTarget] = useState<WorkspaceReelLean | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [renderTheme, setRenderTheme] = useState<RenderTheme>("reel");
  const [batchBusy, setBatchBusy] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setErr(null);
    try {
      const list = await api.workspaceList();
      setItems(list);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const counts = useMemo<Record<Filter, number>>(() => {
    const out: Record<string, number> = { all: items.length };
    for (const s of ALL_STATUSES) out[s] = 0;
    for (const it of items) out[it.status] = (out[it.status] ?? 0) + 1;
    return out as Record<Filter, number>;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filter !== "all" && it.status !== filter) return false;
      if (q && !`${it.title} ${it.id}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filter, search]);

  const onDelete = async (id: string, title: string): Promise<void> => {
    if (!window.confirm(`Reel "${title}" wirklich löschen?`)) return;
    await api.workspaceDelete(id);
    await load();
  };

  const onStatusChange = async (id: string, status: WorkspaceStatus): Promise<void> => {
    await api.workspaceSetStatus(id, status);
    await load();
  };

  // Auswahl beim Filterwechsel auf die sichtbaren Reels eingrenzen.
  const visibleIds = useMemo(() => filtered.map((it) => it.id), [filtered]);
  const selectedVisible = useMemo(
    () => visibleIds.filter((id) => selected.has(id)),
    [visibleIds, selected],
  );
  const allVisibleSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleOne = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = (): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };

  const clearSelection = (): void => setSelected(new Set());

  const onDeleteSelected = async (): Promise<void> => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} ausgewählte Reel(s) wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    setBatchBusy(true);
    try {
      const res = await api.workspaceBatchDelete(ids);
      showToast?.("success", `${res.deleted} Reel(s) gelöscht.`);
      clearSelection();
      await load();
    } catch (e) {
      showToast?.("error", `Löschen fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBatchBusy(false);
    }
  };

  const onRenderSelected = async (): Promise<void> => {
    const ids = [...selected];
    if (!ids.length) return;
    setBatchBusy(true);
    try {
      const res = await api.renderBatch(ids, renderTheme);
      const n = res.created.length;
      showToast?.(
        "success",
        `${n} Render${n === 1 ? "" : "s"} gestartet${
          renderTheme === "both" ? " (dark + light)" : ""
        } · läuft sequenziell in der Queue.`,
      );
      if (res.missing.length) {
        showToast?.("error", `${res.missing.length} Reel(s) nicht gefunden.`);
      }
      clearSelection();
      onShowQueue?.();
    } catch (e) {
      showToast?.("error", `Render fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <div className="workspace">
      <div className="workspace-head">
        <div className="workspace-head-left">
          <h2>Workspace</h2>
          <p className="workspace-sub">
            Alle gespeicherten Reels mit Status-Workflow, Notizen und Change-Requests.
          </p>
        </div>
        <div className="workspace-head-actions">
          <input
            className="workspace-search"
            placeholder="🔍 Suche Titel oder ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn ghost" onClick={() => void load()} disabled={loading}>
            {loading ? "…" : "↻ Refresh"}
          </button>
          <button className="btn primary" onClick={onNew}>
            ＋ Neues Reel
          </button>
        </div>
      </div>

      <div className="workspace-filters">
        {(["all", ...ALL_STATUSES] as Filter[]).map((f) => {
          const meta = f === "all" ? null : STATUS_META[f];
          return (
            <button
              key={f}
              className={`ws-filter ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              style={meta ? { borderColor: meta.color } : undefined}
            >
              {meta ? (
                <span className="ws-filter-dot" style={{ background: meta.color }} />
              ) : null}
              <span>{f === "all" ? "Alle" : meta?.label}</span>
              <span className="ws-filter-count">{counts[f] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {selected.size > 0 && (
        <div className="ws-bulkbar">
          <span className="ws-bulkbar-count">{selected.size} ausgewählt</span>
          <button className="btn ghost sm" onClick={clearSelection}>
            Auswahl leeren
          </button>
          <div className="ws-bulkbar-spacer" />
          <label className="ws-bulkbar-theme">
            Theme
            <select
              value={renderTheme}
              onChange={(e) => setRenderTheme(e.target.value as RenderTheme)}
            >
              <option value="reel">wie gespeichert</option>
              <option value="dark">Dark</option>
              <option value="light">Light</option>
              <option value="both">Dark + Light</option>
            </select>
          </label>
          <button
            className="btn primary"
            onClick={() => void onRenderSelected()}
            disabled={batchBusy}
          >
            {batchBusy ? "Starte…" : `▶ ${selected.size} rendern`}
          </button>
          <button
            className="btn danger"
            onClick={() => void onDeleteSelected()}
            disabled={batchBusy}
            title="Ausgewählte Reels löschen"
          >
            🗑 {selected.size} löschen
          </button>
        </div>
      )}

      {err && <div className="error-row">{err}</div>}

      {filtered.length === 0 ? (
        <div className="workspace-empty">
          <div className="empty-emoji">📭</div>
          <h3>Noch keine Reels gespeichert</h3>
          <p>Lege im Composer ein neues Reel an und speichere es hier.</p>
          <button className="btn primary" onClick={onNew}>
            Im Composer starten
          </button>
        </div>
      ) : (
        <table className="workspace-table">
          <thead>
            <tr>
              <th className="ws-check-col">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedVisible.length > 0 && !allVisibleSelected;
                  }}
                  onChange={toggleAllVisible}
                  title="Alle sichtbaren auswählen"
                />
              </th>
              <th>Titel</th>
              <th>Status</th>
              <th>Format</th>
              <th>Scenes</th>
              <th>Notes</th>
              <th>Change&nbsp;Requests</th>
              <th>Zuletzt geändert</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const meta = STATUS_META[it.status];
              return (
                <tr
                  key={it.id}
                  className={`ws-row ${selected.has(it.id) ? "selected" : ""}`}
                  onClick={() => onOpen(it.id)}
                >
                  <td className="ws-check-col" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(it.id)}
                      onChange={() => toggleOne(it.id)}
                    />
                  </td>
                  <td className="ws-title">
                    <strong>{it.title}</strong>
                    <span className="ws-id">{it.id}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className="ws-status-select"
                      value={it.status}
                      style={{ borderColor: meta.color, color: meta.color }}
                      onChange={(e) =>
                        void onStatusChange(it.id, e.target.value as WorkspaceStatus)
                      }
                    >
                      {ALL_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_META[s].label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className="ws-chip">{it.format}</span>
                  </td>
                  <td>{it.sceneCount}</td>
                  <td>{it.noteCount > 0 ? <span className="ws-pill">📝 {it.noteCount}</span> : "—"}</td>
                  <td>
                    {it.crTotal > 0 ? (
                      <span className={`ws-pill ${it.crOpen > 0 ? "warn" : "good"}`}>
                        ✓ {it.crTotal - it.crOpen}/{it.crTotal}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="ws-date">{formatDate(it.updatedAt)}</td>
                  <td onClick={(e) => e.stopPropagation()} className="ws-actions">
                    <button className="btn ghost sm" onClick={() => onOpen(it.id)}>
                      Öffnen
                    </button>
                    <button
                      className="btn ghost sm"
                      title="Partner-URL einfügen — Inhalte automatisch tauschen, Video bleibt"
                      onClick={() => setPartnerTarget(it)}
                    >
                      🔗
                    </button>
                    <button
                      className="btn danger sm"
                      onClick={() => void onDelete(it.id, it.title)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <PartnerLinkModal
        open={partnerTarget !== null}
        reel={partnerTarget}
        defaultMode="clone"
        onClose={() => setPartnerTarget(null)}
        onApplied={(newId, mode) => {
          setPartnerTarget(null);
          void load();
          if (mode === "clone") {
            onOpen(newId);
          } else {
            onOpen(newId);
          }
        }}
      />
    </div>
  );
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const min = 60_000;
  const h = 60 * min;
  const day = 24 * h;
  if (diff < min) return "gerade eben";
  if (diff < h) return `vor ${Math.round(diff / min)} min`;
  if (diff < day) return `vor ${Math.round(diff / h)} h`;
  if (diff < 7 * day) return `vor ${Math.round(diff / day)} d`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
}
